import { faBars } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Header.css';

import { useState, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { AuthContext } from '../../context/auth-context';
import useDocumentTitle from '../../hooks/title-hook';
import SideDrawer from '../../menu/SideDrawer';
import UserBgImg from './images/profile_1.png';

type HeaderProps = {
  assessmentPage?: string;
  page?: string;
  progress?: number;
  showSideDrawer?: boolean;
  type?: 'transparent' | 'coloured';
  onNextPage?: (dir: string, pi?: number) => void;
};

export function Header({
  assessmentPage,
  page,
  progress,
  showSideDrawer = false,
  type = 'coloured',
  onNextPage,
}: HeaderProps) {
  const [profileImage, setProfileImage] = useState(UserBgImg);

  const { t } = useTranslation();
  const { pathname } = useLocation();

  let pagetitle = pathname.substring(1);

  let description = t('nav.page.defaultDescription');

  if (pagetitle.slice(-1) === '/') {
    pagetitle = pagetitle.slice(0, -1);
  }

  if (pagetitle === 'sign-in') {
    description = t('nav.page.notLoggedInDescription');
  }

  pagetitle = pagetitle
    .split('-')
    .join(' ')
    .split('_')
    .join(' ')
    .split('/')
    .reverse()
    .join(' - ')
    .toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');

  // Remove token inside page title when user is in ResetPasswordOverlay component
  if (pagetitle.includes('Reset')) {
    pagetitle = 'Reset';
  }

  if (assessmentPage) {
    pagetitle = `${assessmentPage} - ${pagetitle}`;
  }

  useDocumentTitle(`${`${pagetitle} - ` || ''}World of Wellbeing - Champion Health`, description);

  const auth = useContext(AuthContext);

  useEffect(() => {
    if (auth.image) {
      setProfileImage(auth.image);
    }
  }, [auth.image]);

  const [drawerIsOpen, setSideDrawerIsOpen] = useState(false);

  const openSideDrawer = useCallback(() => {
    setSideDrawerIsOpen(true);
  }, []);

  const menuClickHandler = useCallback(() => {
    setSideDrawerIsOpen(false);
  }, []);
  return (
    <div className={`header-top-bar ${type}`}>
      {showSideDrawer && auth.access === '10' && (
        <SideDrawer
          drawerIsOpen={drawerIsOpen}
          onMenuClose={menuClickHandler}
          onNextPage={onNextPage}
        />
      )}
      {assessmentPage && (
        <div className="sr-only" role="status" aria-live="assertive">
          {pagetitle}
        </div>
      )}
      <div id="left_nav" />
      <div id="right_nav" className={auth.isLoggedIn ? 'logged-in' : 'logged-out'}>
        {page === 'register' && (
          <a href="/">
            <span className="visuallyHidden">{t('nav.link.logo')}</span>
            <div className="register_page_logo" />
          </a>
        )}

        <div id="progress_bar_container">
          {page === 'assessment' && (
            <>
              <div id="progress_bar_outer">
                <div id="progress_bar" style={{ width: `${progress}%` }} />
              </div>
              <p>
                <span id="progress_percentage">{progress}</span>% {t('A124')}
              </p>
            </>
          )}
        </div>

        {showSideDrawer && auth.access === '10' && (
          <div
            id="menu_button"
            role="button"
            onClick={openSideDrawer}
            onKeyDown={openSideDrawer}
            tabIndex={0}
          >
            <FontAwesomeIcon icon={faBars} className="icon" />
          </div>
        )}

        {auth.isLoggedIn ? (
          <Link className="headerSettingsLink" to="/profile/settings">
            <span className="visuallyHidden">{t('nav.page.profileUpdate')}</span>
            <div
              className="fs-exclude"
              id="photo-outer"
              style={{ backgroundImage: `url(${profileImage})` }}
            />
          </Link>
        ) : (
          ''
        )}
      </div>
    </div>
  );
}
